import * as React from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './CallReviews.css'; 
import { FormLabel, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import axios from 'axios'; 
import { API_URL } from "../../utils/auth";
import Auth from '../../utils/auth';
import { formatDate } from '../../utils/dateFormat';

export default function CallReviews({ clientId, reducedHeight = false }) {
  const [callReviews, setCallReviews] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(true);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [selectedCall, setSelectedCall] = React.useState(null);
  const [editDate, setEditDate] = React.useState('');
  const navigate = useNavigate();
  const workspaceId = Auth.getWorkspaceId();

  React.useEffect(() => {
    if (clientId && workspaceId) {
      axios
        .get(`${API_URL}/call-score/get-call-scores`, {
          params: {
            clientId: clientId,
            workspaceId: workspaceId
          }
        })
        .then((response) => {
          setCallReviews(response.data);
          setLoading(false);
        })
        .catch((error) => {
          if (error.response?.status === 404) {
            setCallReviews([]);
          } else {
            console.error('Error fetching call reviews:', error);
          }
          setLoading(false);
        });
    }
  }, [clientId, workspaceId]);

  const handleNewCall = () => {
    navigate(`/calls/${clientId}`, { 
      state: {
        workspaceId: workspaceId
      },
      replace: true 
    });
  };

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  const handleEditClick = (call) => {
    const displayDate = call.call_date || call.created_at;
    setSelectedCall(call);
    let yyyyMmDd = '';
    if (displayDate) {
      const d = new Date(displayDate);
      if (!isNaN(d.getTime())) {
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        yyyyMmDd = `${y}-${m}-${day}`;
      }
    }
    setEditDate(yyyyMmDd);
    setEditDialogOpen(true);
  };

  const handleSaveEditDate = async () => {
    if (!selectedCall || !workspaceId) return;

    try {
      const callDateISO = editDate ? new Date(editDate).toISOString() : null;
      await axios.put(`${API_URL}/call-score/update-call-score-date`, {
        id: selectedCall.id,
        callDate: callDateISO,
        workspaceId: workspaceId
      });

      const updatedReviews = callReviews.map(review => 
        review.id === selectedCall.id 
          ? { ...review, call_date: callDateISO }
          : review
      );
      setCallReviews(updatedReviews);
      setEditDialogOpen(false);
      setSelectedCall(null);
      setEditDate('');
    } catch (error) {
      console.error('Error updating call date:', error);
      alert('Failed to update call date. Please try again.');
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCall(null);
    setEditDate('');
  };

  return (
    <Card sx={{ 
      width: '100%', 
      maxWidth: 800, 
      boxShadow: 'none', 
      border: 'none', 
      borderRadius: '2%', 
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'height 0.3s ease'
    }}>
      <CardContent sx={{ padding: '0 !important' }}>
        <Accordion 
          expanded={expanded}
          onChange={handleAccordionChange}
          sx={{ 
            boxShadow: 'none', 
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              margin: 0
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              padding: '16px',
              '& .MuiAccordionSummary-content': {
                margin: 0,
                alignItems: 'center',
                justifyContent: 'space-between'
              }
            }}
          >
            <FormLabel sx={{ fontSize: '1.4rem', color: 'black', fontWeight: 'bold', margin: 0 }}>
              Call Reviews
            </FormLabel>
            <Button
              className="purple-button"
              sx={{
                "&:hover": {
                  backgroundColor: "#4A21D1",
                },
                marginRight: '32px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleNewCall();
              }}
            >
              New Call
            </Button>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0 16px 16px 16px' }}>
            <List>
              <Box className="list-container" sx={{ marginTop: '20px', marginLeft: '2%', maxHeight: reducedHeight ? '250px' : '315px', overflowY: 'auto'}}>
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  callReviews.map((item, index) => (
                    <Box key={index} className="row-box" sx={{ mb:1, fontFamily: "Albert Sans", fontWeight: "bold", padding: '8px', borderRadius: '10px', backgroundColor: 'rgba(246, 244, 255, 1)' }}>
                      <ListItem
                        className="list-item"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0",
                          gap: '8px',
                          borderRadius:'10px',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: '32px' }}>
                          <img 
                            src="/call_end.png" 
                            alt="Call end"
                            style={{ width: '24px', height: '24px' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ color: 'rgba(41, 27, 59, 0.4)', fontWeight: '600' }}>Call on</span>
                              <span style={{ color: 'black', fontWeight: '600' }}>{formatDate(item.call_date || item.created_at)}</span>
                            </Box>
                          }
                          sx={{ margin: 0 }}
                        />
                        <Button
                          variant="text"
                          size="small"
                          sx={{
                            textTransform: 'none', 
                            fontSize: '0.875rem', 
                            color: 'rgba(70, 13, 149, 1)',
                            fontWeight: '400',
                            marginRight: '8px'
                          }}
                          onClick={() => handleEditClick(item)} 
                        >
                          Edit
                        </Button>
                        <Button
                        variant="text"
                        size="small"
                        className="view-button"
                        sx={{
                          textTransform: 'none', 
                          fontSize: '0.875rem', 
                          color: 'rgba(70, 13, 149, 1)',
                          fontWeight: '400' 
                        }}
                        onClick={() => navigate(`/calls/${clientId}/${item.id}`)} 
                      >
                        View
                      </Button>
                      </ListItem>
                    </Box>
                  ))
                )}
              </Box>
            </List>
          </AccordionDetails>
        </Accordion>
      </CardContent>
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: '500px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold', paddingBottom: '8px' }}>Edit Call Date</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Call Date"
              value={editDate ? dayjs(editDate) : null}
              onChange={(newValue) => setEditDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
              format="MMM D, YYYY"
              slotProps={{ textField: { fullWidth: true, sx: { marginTop: '16px' } } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseEditDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button 
            onClick={handleSaveEditDate} 
            variant="contained" 
            sx={{ 
              backgroundColor: "#4A21D1", 
              "&:hover": { 
                backgroundColor: "#3a1aa1" 
              },
              textTransform: 'none',
              padding: '8px 24px'
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
